import {useDeferredValue, useEffect, useMemo, useRef, useState} from 'react';

import {
    Box,
    ButtonBase,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    ClickAwayListener,
    CircularProgress,
    Dialog,
    DialogContent,
    FormControl,
    InputBase,
    ListSubheader,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Popper,
    Select,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';

import BrandProfileEditor from '../../shared/brand/BrandProfileEditor';
import {ensureMetafieldDefinitions, saveBrandProfile} from '../../shared/brand/brandService';
import {C} from '../constants';

const normalizeOption = (option) => {
    const value = typeof option === 'string' ? option : option.value;
    const label =
        typeof option === 'string' ? option : option.label || option.name || option.value;
    const searchText =
        typeof option === 'string'
            ? option
            : option.searchText || option.search || option.label || option.name || option.value;

    return {value, label, searchText};
};

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const normalizeSqlLikePattern = (pattern) =>
    pattern
        .trim()
        .replace(/^(?:[\w.]+\s+)?like\s+/i, '')
        .replace(/^['"]|['"]$/g, '');

export const matchesSqlLikePattern = (candidate, rawPattern) => {
    const pattern = normalizeSqlLikePattern(rawPattern);
    const normalizedCandidate = String(candidate || '');

    if (!pattern) return true;

    if (!/[%_]/.test(pattern)) {
        return normalizedCandidate.toLowerCase().includes(pattern.toLowerCase());
    }

    const regex = new RegExp(
        `^${escapeRegExp(pattern).replace(/%/g, '.*').replace(/_/g, '.')}$`,
        'i',
    );

    return regex.test(normalizedCandidate);
};

const filterOptions = (options, filterText, filterMode = 'contains') => {
    const trimmedFilter = filterText.trim();
    if (!trimmedFilter) return options;

    const patterns = trimmedFilter
        .split(/[,\n;]+/)
        .map((part) => part.trim())
        .filter(Boolean);

    if (!patterns.length) return options;

    return options.filter((option) => {
        const containsHaystacks = [
            option.searchText,
            option.label,
            option.value,
        ]
            .filter(Boolean)
            .map((value) => String(value));
        const sqlHaystacks = [option.label, option.value]
            .filter(Boolean)
            .map((value) => String(value));

        return patterns.some((pattern) => {
            const haystacks = filterMode === 'sql-like' ? sqlHaystacks : containsHaystacks;
            return haystacks.some((candidate) =>
                filterMode === 'sql-like'
                    ? matchesSqlLikePattern(candidate, pattern)
                    : candidate.toLowerCase().includes(pattern.toLowerCase()),
            );
        });
    });
};

const outlinedInputSx = {
    backgroundColor: C.surfaceRaised,
    color: C.ink,
    borderRadius: 2,
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: C.border2,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: C.border2,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: C.amber,
    },
    '& .MuiSelect-select': {
        display: 'flex',
        alignItems: 'center',
        minHeight: '20px',
        py: '9px',
    },
};

const menuProps = {
    PaperProps: {
        sx: {
            mt: 0.75,
            borderRadius: 1.5,
            border: `1px solid ${C.border2}`,
            backgroundColor: C.surfaceRaised,
            backgroundImage: 'none',
            boxShadow: C.shadow,
        },
    },
};

export function Label({children, style}) {
    return (
        <Typography
            component="div"
            style={style}
            sx={{
                fontSize: 10,
                fontWeight: 500,
                color: C.textSoft,
                textTransform: 'uppercase',
                letterSpacing: '.07em',
            }}>
            {children}
        </Typography>
    );
}

export function Field({label, children, style}) {
    return (
        <Box style={style} sx={{display: 'flex', flexDirection: 'column', gap: '5px'}}>
            <Label>{label}</Label>
            {children}
        </Box>
    );
}

export function FInput({value, onChange, placeholder}) {
    return (
        <TextField
            fullWidth
            size="small"
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            variant="outlined"
            sx={{
                '& .MuiOutlinedInput-root': outlinedInputSx,
                '& .MuiInputBase-input::placeholder': {
                    color: C.textMuted,
                    opacity: 1,
                },
            }}
        />
    );
}

export function FSelect({
    value,
    onChange,
    options,
    disabled = false,
    filterable = false,
    filterPlaceholder = 'Filter options',
    noMatchesLabel = 'No matching options',
    menuMaxHeight = 320,
}) {
    const [menuOpen, setMenuOpen] = useState(false);
    const [filterText, setFilterText] = useState('');
    const normalizedOptions = useMemo(() => options.map(normalizeOption), [options]);
    const filteredOptions = useMemo(
        () => filterOptions(normalizedOptions, filterText),
        [filterText, normalizedOptions],
    );
    const mergedMenuProps = {
        ...menuProps,
        PaperProps: {
            ...menuProps.PaperProps,
            sx: {
                ...menuProps.PaperProps?.sx,
                maxHeight: menuMaxHeight,
            },
        },
        MenuListProps: {
            dense: true,
            sx: {py: 0},
        },
        disableAutoFocusItem: filterable,
    };

    return (
        <FormControl fullWidth size="small">
            <Select
                value={value}
                onChange={onChange}
                disabled={disabled}
                open={menuOpen}
                onOpen={() => setMenuOpen(true)}
                onClose={() => {
                    setMenuOpen(false);
                    setFilterText('');
                }}
                // displayEmpty lets MUI render the label of an option whose
                // value is the empty string (e.g. an "All" / "Any" default).
                // Without it, MUI treats "" as "no selection" and shows
                // nothing in the trigger even when an empty-value MenuItem
                // is technically selected.
                displayEmpty
                MenuProps={mergedMenuProps}
                renderValue={(selectedValue) =>
                    normalizedOptions.find((option) => option.value === selectedValue)
                        ?.label || ''
                }
                sx={outlinedInputSx}>
                {filterable && (
                    <ListSubheader
                        disableSticky
                        sx={{
                            backgroundColor: C.surfaceRaised,
                            borderBottom: `1px solid ${C.border}`,
                            px: 1,
                            py: 1,
                        }}>
                        <TextField
                            autoFocus
                            fullWidth
                            size="small"
                            value={filterText}
                            placeholder={filterPlaceholder}
                            onChange={(event) => setFilterText(event.target.value)}
                            onKeyDown={(event) => event.stopPropagation()}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    ...outlinedInputSx,
                                    fontSize: 12,
                                },
                                '& .MuiInputBase-input': {py: '7px'},
                                '& .MuiInputBase-input::placeholder': {
                                    color: C.textMuted,
                                    opacity: 1,
                                },
                            }}
                        />
                    </ListSubheader>
                )}
                {filteredOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                        {option.label}
                    </MenuItem>
                ))}
                {!filteredOptions.length && (
                    <MenuItem disabled sx={{fontSize: 12, color: C.textMuted}}>
                        {noMatchesLabel}
                    </MenuItem>
                )}
            </Select>
        </FormControl>
    );
}

export function FMultiSelect({
    value = [],
    onChange,
    options,
    placeholder = 'Select options',
    emptyLabel = 'No options available',
    filterable = false,
    filterMode = 'contains',
    filterPlaceholder = 'Filter options',
    filterHint = '',
    noMatchesLabel = 'No matching options',
    menuWidth = 320,
    menuMaxHeight = 320,
    optionRenderBatchSize = 120,
    onFilterStateChange = null,
    filterActions = [],
}) {
    const [menuOpen, setMenuOpen] = useState(false);
    const [filterText, setFilterText] = useState('');
    const [visibleOptionCount, setVisibleOptionCount] = useState(optionRenderBatchSize);
    const deferredFilterText = useDeferredValue(filterText);
    const normalizedOptions = useMemo(() => options.map(normalizeOption), [options]);
    const filteredOptions = useMemo(
        () => filterOptions(normalizedOptions, deferredFilterText, filterMode),
        [deferredFilterText, filterMode, normalizedOptions],
    );
    const selectedOptions = useMemo(
        () => normalizedOptions.filter((option) => value.includes(option.value)),
        [normalizedOptions, value],
    );
    const visibleOptions = useMemo(
        () => filteredOptions.slice(0, visibleOptionCount),
        [filteredOptions, visibleOptionCount],
    );
    const filteredOptionValues = useMemo(
        () => filteredOptions.map((option) => option.value),
        [filteredOptions],
    );
    const filteredSelectedCount = useMemo(
        () => filteredOptionValues.filter((optionValue) => value.includes(optionValue)).length,
        [filteredOptionValues, value],
    );
    const hasMoreFilteredOptions = visibleOptions.length < filteredOptions.length;
    const allFilteredSelected =
        filteredOptionValues.length > 0 && filteredSelectedCount === filteredOptionValues.length;

    useEffect(() => {
        setVisibleOptionCount(optionRenderBatchSize);
    }, [deferredFilterText, menuOpen, optionRenderBatchSize]);

    useEffect(() => {
        if (!onFilterStateChange) return;

        onFilterStateChange({
            filterText: deferredFilterText,
            filteredValues: filteredOptionValues,
        });
    }, [deferredFilterText, filteredOptionValues, onFilterStateChange]);

    const loadMoreOptions = () =>
        setVisibleOptionCount((current) =>
            Math.min(current + optionRenderBatchSize, filteredOptions.length),
        );

    const updateFilteredSelection = (nextCheckedState) => {
        if (!filteredOptionValues.length) return;

        if (nextCheckedState) {
            onChange(Array.from(new Set([...value, ...filteredOptionValues])));
            return;
        }

        const filteredValueSet = new Set(filteredOptionValues);
        onChange(value.filter((optionValue) => !filteredValueSet.has(optionValue)));
    };

    const mergedMenuProps = {
        ...menuProps,
        PaperProps: {
            ...menuProps.PaperProps,
            onScroll: (event) => {
                const target = event.currentTarget;
                if (
                    hasMoreFilteredOptions &&
                    target.scrollTop + target.clientHeight >= target.scrollHeight - 32
                ) {
                    loadMoreOptions();
                }
            },
            sx: {
                ...menuProps.PaperProps?.sx,
                width: menuWidth,
                maxWidth: 'calc(100vw - 32px)',
                maxHeight: menuMaxHeight,
            },
        },
        MenuListProps: {
            dense: true,
            sx: {
                py: 0,
            },
        },
        disableAutoFocusItem: filterable,
    };

    return (
        <FormControl fullWidth size="small">
            <Select
                multiple
                displayEmpty
                open={menuOpen}
                value={value}
                onOpen={() => setMenuOpen(true)}
                onClose={() => {
                    setMenuOpen(false);
                    setFilterText('');
                    setVisibleOptionCount(optionRenderBatchSize);
                }}
                onChange={(event) => {
                    const nextValue = event.target.value;
                    onChange(typeof nextValue === 'string' ? nextValue.split(',') : nextValue);
                }}
                input={<OutlinedInput />}
                MenuProps={mergedMenuProps}
                renderValue={(selected) => {
                    if (!selected.length) {
                        return (
                            <Typography sx={{fontSize: 13, color: C.textMuted}}>
                                {placeholder}
                            </Typography>
                        );
                    }

                    return (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                flexWrap: 'wrap',
                                minWidth: 0,
                            }}>
                            {selectedOptions.slice(0, 2).map((option) => (
                                <Chip
                                    key={option.value}
                                    label={option.label}
                                    size="small"
                                    sx={{
                                        maxWidth: '100%',
                                        height: 22,
                                        backgroundColor: C.mist,
                                        border: `1px solid ${C.border}`,
                                        color: C.ink,
                                        '& .MuiChip-label': {
                                            px: 1,
                                            fontSize: 11,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        },
                                    }}
                                />
                            ))}
                            {selectedOptions.length > 2 && (
                                <Typography
                                    sx={{
                                        fontSize: 11,
                                        color: C.textMuted,
                                        whiteSpace: 'nowrap',
                                    }}>
                                    +{selectedOptions.length - 2} more
                                </Typography>
                            )}
                        </Box>
                    );
                }}
                sx={{
                    ...outlinedInputSx,
                    '& .MuiSelect-select': {
                        minHeight: 24,
                        py: '8px',
                    },
                }}>
                {filterable && (
                    <ListSubheader
                        disableSticky
                        sx={{
                            backgroundColor: C.surfaceRaised,
                            borderBottom: `1px solid ${C.border}`,
                            px: 1,
                            py: 1,
                        }}>
                        <TextField
                            autoFocus
                            fullWidth
                            size="small"
                            value={filterText}
                            placeholder={filterPlaceholder}
                            onChange={(event) => setFilterText(event.target.value)}
                            onKeyDown={(event) => event.stopPropagation()}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    ...outlinedInputSx,
                                    fontSize: 12,
                                },
                                '& .MuiInputBase-input': {
                                    py: '7px',
                                },
                                '& .MuiInputBase-input::placeholder': {
                                    color: C.textMuted,
                                    opacity: 1,
                                },
                            }}
                        />
                        {filterHint ? (
                            <Typography
                                sx={{
                                    mt: 0.75,
                                    fontSize: 10,
                                    lineHeight: 1.4,
                                    color: C.textSoft,
                                    whiteSpace: 'normal',
                                }}>
                                {filterHint}
                            </Typography>
                        ) : null}
                        <Box
                            sx={{
                                mt: 0.75,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                                flexWrap: 'wrap',
                            }}>
                            <Typography sx={{fontSize: 10, color: C.textSoft}}>
                                {filteredOptions.length
                                    ? `${filteredSelectedCount}/${filteredOptions.length} filtered selected`
                                    : 'No filtered SKUs'}
                            </Typography>
                            <Box sx={{display: 'flex', gap: 0.75, flexWrap: 'wrap', justifyContent: 'flex-end'}}>
                                <Button
                                    size="small"
                                    onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        updateFilteredSelection(true);
                                    }}
                                    disabled={!filteredOptionValues.length || allFilteredSelected}
                                    sx={{
                                        minWidth: 0,
                                        px: 1,
                                        py: 0.35,
                                        fontSize: 10.5,
                                        borderRadius: 999,
                                        border: `1px solid ${C.border}`,
                                        color: C.textMuted,
                                    }}>
                                    Select filtered
                                </Button>
                                <Button
                                    size="small"
                                    onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        updateFilteredSelection(false);
                                    }}
                                    disabled={!filteredSelectedCount}
                                    sx={{
                                        minWidth: 0,
                                        px: 1,
                                        py: 0.35,
                                        fontSize: 10.5,
                                        borderRadius: 999,
                                        border: `1px solid ${C.border}`,
                                        color: C.textMuted,
                                    }}>
                                    Deselect filtered
                                </Button>
                                {filterActions.map((action) => (
                                    <Button
                                        key={action.key || action.label}
                                        size="small"
                                        onClick={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            action.onClick?.(event);
                                        }}
                                        disabled={action.disabled}
                                        sx={{
                                            minWidth: 0,
                                            px: 1,
                                            py: 0.35,
                                            fontSize: 10.5,
                                            borderRadius: 999,
                                            border: `1px solid ${C.border}`,
                                            color: C.textMuted,
                                            ...(action.sx || {}),
                                        }}>
                                        {action.label}
                                    </Button>
                                ))}
                            </Box>
                        </Box>
                    </ListSubheader>
                )}
                {visibleOptions.length ? (
                    visibleOptions.map((option) => {
                        const checked = value.includes(option.value);

                        return (
                            <MenuItem key={option.value} value={option.value} sx={{minHeight: 34}}>
                                <Checkbox checked={checked} size="small" />
                                <ListItemText
                                    primary={option.label}
                                    primaryTypographyProps={{
                                        fontSize: 12.5,
                                        color: C.ink,
                                    }}
                                />
                            </MenuItem>
                        );
                    })
                ) : (
                    <MenuItem disabled>
                        {deferredFilterText.trim() ? noMatchesLabel : emptyLabel}
                    </MenuItem>
                )}
                {hasMoreFilteredOptions && (
                    <MenuItem
                        disabled
                        sx={{
                            minHeight: 34,
                            justifyContent: 'center',
                            fontSize: 11,
                            color: C.textSoft,
                        }}>
                        Scroll to load {filteredOptions.length - visibleOptions.length} more
                    </MenuItem>
                )}
            </Select>
        </FormControl>
    );
}

export function Spinner({dark}) {
    return (
        <CircularProgress
            size={13}
            thickness={6}
            sx={{
                color: dark ? C.ink : '#fff',
            }}
        />
    );
}

export function Badge({color, label, count}) {
    return (
        <Stack direction="row" alignItems="center" spacing={0.75}>
            <Box
                sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: count > 0 ? color : C.textFaint,
                    flexShrink: 0,
                }}
            />
            <Typography
                sx={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: count > 0 ? color : C.textFaint,
                }}>
                {label}
            </Typography>
            {count > 0 && (
                <Chip
                    label={count}
                    size="small"
                    sx={{
                        height: 18,
                        backgroundColor: `${color}18`,
                        color,
                        '& .MuiChip-label': {
                            px: 0.75,
                            fontSize: 10,
                            fontWeight: 600,
                        },
                    }}
                />
            )}
        </Stack>
    );
}

export function OutCard({label, dotColor, labelColor, delay, actions, children}) {
    return (
        <Card
            elevation={0}
            sx={{
                position: 'relative',
                backgroundColor: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: '10px',
                animation: `popIn .25s ease ${delay * 0.08}s both`,
            }}>
            <CardContent sx={{p: '13px 15px !important'}}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                    sx={{
                        mb: 1,
                        color: labelColor,
                        fontSize: 10,
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        letterSpacing: '.07em',
                    }}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Box
                            sx={{
                                width: 5,
                                height: 5,
                                borderRadius: '50%',
                                backgroundColor: dotColor,
                                flexShrink: 0,
                            }}
                        />
                        <Typography
                            component="span"
                            sx={{
                                fontSize: 10,
                                fontWeight: 500,
                                textTransform: 'uppercase',
                                letterSpacing: '.07em',
                                color: 'inherit',
                            }}>
                            {label}
                        </Typography>
                    </Stack>
                    {actions ? (
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                            {actions}
                        </Stack>
                    ) : null}
                </Stack>
                {children}
            </CardContent>
        </Card>
    );
}

export function CopyBtn({id, text, copied, onCopy, inline = false}) {
    return (
        <button
            onClick={() => onCopy(id, text)}
            style={{
                position: inline ? 'static' : 'absolute',
                top: inline ? 'auto' : 10,
                right: inline ? 'auto' : 10,
                fontFamily: 'inherit',
                fontSize: 10.5,
                fontWeight: 600,
                padding: '4px 10px',
                borderRadius: 7,
                border: `1px solid ${C.border}`,
                background: C.surfaceRaised,
                color: copied[id] ? C.teal : C.textSoft,
                cursor: 'pointer',
                lineHeight: 1.2,
            }}>
            {copied[id] ? 'copied!' : 'copy'}
        </button>
    );
}

export function EmptyState({
    hasProduct = false,
    productName = '',
    selectedCount = 0,
    enabled = {},
    tone = '',
    useCase = '',
    audience = '',
}) {
    const sectionLabels = [
        enabled.title ? 'Title' : null,
        enabled.desc ? 'Description' : null,
        enabled.bullets ? 'Bullets' : null,
    ].filter(Boolean);
    const selectionLabel =
        selectedCount > 1 ? `${selectedCount} selected SKUs` : '1 selected SKU';
    const title = hasProduct ? 'Ready to generate' : 'Set up generator';
    const helperText = hasProduct
        ? `Review the setup on the left, then generate content for ${productName || 'this SKU'}.`
        : 'Select a profile and product, then generate content.';

    return (
        <Card
            sx={{
                borderRadius: '14px',
                border: `1px solid ${C.border}`,
                background: `linear-gradient(135deg, ${C.surface} 0%, ${C.surfaceAlt} 100%)`,
                boxShadow: '0 18px 40px rgba(17, 24, 39, 0.04)',
                animation: 'popIn .2s ease both',
            }}>
            <CardContent
                sx={{
                    p: '18px !important',
                    display: 'grid',
                    gap: 1.5,
                }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                        sx={{
                            width: 42,
                            height: 42,
                            borderRadius: '12px',
                            border: `1px solid ${C.border}`,
                            background: `linear-gradient(135deg, ${C.tealL} 0%, ${C.surface} 100%)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}>
                        <Typography sx={{fontSize: 19, lineHeight: 1, color: C.teal}}>
                            ✦
                        </Typography>
                    </Box>
                    <Box sx={{minWidth: 0}}>
                        <Typography sx={{fontSize: 14.5, fontWeight: 700, color: C.ink}}>
                            {title}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: 12.5,
                                color: C.textSoft,
                                lineHeight: 1.55,
                                mt: 0.25,
                            }}>
                            {helperText}
                        </Typography>
                    </Box>
                </Stack>

                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Chip
                        size="small"
                        label={selectionLabel}
                        sx={{
                            bgcolor: C.surface,
                            border: `1px solid ${C.border}`,
                            color: C.textMuted,
                        }}
                    />
                    {tone && (
                        <Chip
                            size="small"
                            label={`Tone ${tone}`}
                            sx={{
                                bgcolor: `${C.blue}12`,
                                border: `1px solid ${C.blue}22`,
                                color: C.blue,
                            }}
                        />
                    )}
                    {useCase && (
                        <Chip
                            size="small"
                            label={`Use case ${useCase}`}
                            sx={{
                                bgcolor: `${C.amber}12`,
                                border: `1px solid ${C.amber}22`,
                                color: C.amber,
                            }}
                        />
                    )}
                </Stack>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                        gap: 10,
                    }}>
                    <Box
                        sx={{
                            p: '10px 12px',
                            borderRadius: '10px',
                            background: C.surface,
                            border: `1px solid ${C.border}`,
                        }}>
                        <Typography
                            sx={{
                                fontSize: 10,
                                fontWeight: 700,
                                letterSpacing: '.08em',
                                textTransform: 'uppercase',
                                color: C.textFaint,
                                mb: 0.5,
                            }}>
                            Sections
                        </Typography>
                        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                            {sectionLabels.map((label) => (
                                <Chip
                                    key={label}
                                    size="small"
                                    label={label}
                                    sx={{
                                        height: 24,
                                        bgcolor: `${C.teal}10`,
                                        border: `1px solid ${C.teal}22`,
                                        color: C.teal,
                                    }}
                                />
                            ))}
                        </Stack>
                    </Box>
                    <Box
                        sx={{
                            p: '10px 12px',
                            borderRadius: '10px',
                            background: C.surface,
                            border: `1px solid ${C.border}`,
                        }}>
                        <Typography
                            sx={{
                                fontSize: 10,
                                fontWeight: 700,
                                letterSpacing: '.08em',
                                textTransform: 'uppercase',
                                color: C.textFaint,
                                mb: 0.5,
                            }}>
                            Audience
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: 12.5,
                                color: C.ink,
                                lineHeight: 1.55,
                            }}>
                            {audience || 'General audience'}
                        </Typography>
                    </Box>
                </Box>

                <Box
                    sx={{
                        p: '11px 13px',
                        borderRadius: '10px',
                        background: `${C.surface}cc`,
                        border: `1px dashed ${C.border2}`,
                    }}>
                    <Typography
                        sx={{
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: '.08em',
                            textTransform: 'uppercase',
                            color: C.textFaint,
                            mb: 0.5,
                        }}>
                        Next step
                    </Typography>
                    <Typography sx={{fontSize: 12.5, color: C.textSoft, lineHeight: 1.6}}>
                        Use the left panel to review formulas, grouping, and optional context, then
                        click <strong style={{color: C.ink}}>Generate content</strong>.
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
}

export function ProfileNameField({name, active, color, onChange}) {
    const [editing, setEditing] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [value, setValue] = useState(name);

    useEffect(() => {
        setValue(name);
    }, [name]);

    if (editing) {
        return (
            <InputBase
                value={value}
                autoFocus
                onChange={(event) => setValue(event.target.value)}
                onBlur={() => {
                    onChange(value || name);
                    setEditing(false);
                }}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === 'Escape') {
                        onChange(value || name);
                        setEditing(false);
                    }
                }}
                onClick={(event) => event.stopPropagation()}
                sx={{
                    flex: 1,
                    fontSize: 12.5,
                    fontWeight: active ? 500 : 400,
                    color: active ? color : C.textMuted,
                    '& input': {
                        p: 0,
                    },
                }}
            />
        );
    }

    return (
        <Box
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onDoubleClick={(event) => {
                event.stopPropagation();
                setEditing(true);
            }}
            title="Double-click to rename"
            sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                minWidth: 0,
                cursor: 'pointer',
                userSelect: 'none',
            }}>
            <Typography
                component="span"
                sx={{
                    fontSize: 12.5,
                    fontWeight: active ? 500 : 400,
                    color: active ? color : C.textMuted,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}>
                {name}
            </Typography>
            {hovered && (
                <Typography
                    component="span"
                    sx={{fontSize: 11, color: C.textFaint, flexShrink: 0}}>
                    ✎
                </Typography>
            )}
        </Box>
    );
}

export function ApiKeyBanner({theme = 'light', toggleTheme}) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                px: 2.5,
                py: 1,
                backgroundColor: C.surface,
                borderBottom: `1px solid ${C.border}`,
                flexShrink: 0,
            }}>
            <Typography
                sx={{
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: '.08em',
                    textTransform: 'uppercase',
                    color: C.textSoft,
                }}>
                Workspace Theme
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                    label={theme === 'dark' ? 'Dark' : 'Light'}
                    size="small"
                    sx={{
                        height: 22,
                        backgroundColor: theme === 'dark' ? C.blueL : C.amberL,
                        color: theme === 'dark' ? C.blue : C.amber,
                        '& .MuiChip-label': {
                            px: 1,
                            fontSize: 11,
                            fontWeight: 700,
                        },
                    }}
                />
                <Switch checked={theme === 'dark'} onChange={() => toggleTheme()} />
            </Stack>
        </Box>
    );
}

export function WorkspaceHeader({
    workspace = 'generator',
    onWorkspaceChange,
    apiKey = '',
    onApiKeyChange,
    theme = 'light',
    toggleTheme,
    brandProfile = null,
    onBrandProfileChange,
    currentUser = null,
    onLogout,
}) {
    const [brandDialogOpen, setBrandDialogOpen] = useState(false);
    const [brandDraft, setBrandDraft] = useState(null);
    const [brandSaveError, setBrandSaveError] = useState('');
    const [brandSaving, setBrandSaving] = useState(false);

    const openBrandDialog = () => {
        setBrandDraft(brandProfile ? {...brandProfile} : null);
        setBrandSaveError('');
        setBrandDialogOpen(true);
    };

    const handleBrandSave = async () => {
        if (!brandDraft) return;
        setBrandSaving(true);
        setBrandSaveError('');

        try {
            const saved = await saveBrandProfile(brandDraft);
            if (saved.shopifyMapping) {
                ensureMetafieldDefinitions(saved.shopifyMapping).catch(() => {});
            }
            onBrandProfileChange?.(saved);
            setBrandDialogOpen(false);
        } catch (error) {
            setBrandSaveError(error.message || 'Failed to save the brand profile.');
        } finally {
            setBrandSaving(false);
        }
    };
    const workspaceOptions = [
        {id: 'generator', label: 'Generator', accent: C.blue, accentBg: C.blueL},
        {id: 'review', label: 'Review', accent: C.amber, accentBg: C.amberL},
    ];
    const usersActive = workspace === 'users';

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                px: {xs: 1.5, md: 2.5},
                py: 1,
                backgroundColor: C.surface,
                borderBottom: `1px solid ${C.border}`,
                flexWrap: 'wrap',
            }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
                <Typography
                    sx={{
                        fontFamily: "'Playfair Display',Georgia,serif",
                        fontSize: 18,
                        color: C.ink,
                        lineHeight: 1,
                        whiteSpace: 'nowrap',
                    }}>
                    Content Workspace
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        gap: '3px',
                        background: C.surfaceAlt,
                        border: `1px solid ${C.border}`,
                        borderRadius: 2,
                        padding: '3px',
                    }}>
                    {workspaceOptions.map((option) => {
                        const active = workspace === option.id;
                        return (
                            <Button
                                key={option.id}
                                onClick={() => onWorkspaceChange?.(option.id)}
                                sx={{
                                    minWidth: 90,
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: 1.5,
                                    border: `1px solid ${active ? `${option.accent}44` : 'transparent'}`,
                                    backgroundColor: active ? option.accentBg : 'transparent',
                                    color: active ? option.accent : C.textSoft,
                                    fontSize: 13,
                                    fontWeight: active ? 700 : 500,
                                    '&:hover': {
                                        backgroundColor: active ? option.accentBg : C.surfaceRaised,
                                        color: active ? option.accent : C.ink,
                                    },
                                }}>
                                {option.label}
                            </Button>
                        );
                    })}
                </Box>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
                {brandProfile && (
                    <ButtonBase
                        onClick={openBrandDialog}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '5px 12px',
                            borderRadius: '10px',
                            border: `1px solid ${C.border}`,
                            background: C.surfaceAlt,
                            cursor: 'pointer',
                            '&:hover': {background: C.surfaceRaised},
                            transition: 'background .15s',
                        }}>
                        <Box sx={{
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            background: `linear-gradient(135deg, ${C.teal} 0%, ${C.blue} 100%)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            fontSize: 11,
                            fontWeight: 800,
                            flexShrink: 0,
                        }}>
                            {(brandProfile.companyName || 'B')[0].toUpperCase()}
                        </Box>
                        <Typography sx={{fontSize: 12, fontWeight: 600, color: C.ink, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                            {brandProfile.companyName || 'Brand'}
                        </Typography>
                    </ButtonBase>
                )}

                <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography sx={{fontSize: 11, color: C.textMuted}}>
                        {theme === 'dark' ? 'Dark' : 'Light'}
                    </Typography>
                    <Switch checked={theme === 'dark'} onChange={() => toggleTheme?.()} size="small" />
                </Stack>

                {currentUser ? (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            pl: 1,
                            borderLeft: `1px solid ${C.border}`,
                        }}>
                        <ButtonBase
                            onClick={() => onWorkspaceChange?.('users')}
                            sx={{
                                maxWidth: 130,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                borderRadius: '8px',
                                padding: '5px 8px',
                                fontSize: 12,
                                fontWeight: 700,
                                color: usersActive ? C.purple : C.ink,
                                backgroundColor: usersActive ? C.purpleL : 'transparent',
                                border: `1px solid ${usersActive ? `${C.purple}44` : 'transparent'}`,
                                fontFamily: 'inherit',
                                cursor: 'pointer',
                                '&:hover': {
                                    backgroundColor: usersActive ? C.purpleL : C.surfaceRaised,
                                    color: usersActive ? C.purple : C.ink,
                                },
                            }}>
                            {currentUser.username}
                        </ButtonBase>
                        <Button
                            onClick={() => onLogout?.()}
                            sx={{
                                minWidth: 0,
                                px: 1.25,
                                py: 0.5,
                                borderRadius: 1.5,
                                border: `1px solid ${C.border}`,
                                color: C.textMuted,
                                fontSize: 12,
                                '&:hover': {
                                    backgroundColor: C.surfaceRaised,
                                    color: C.ink,
                                },
                            }}>
                            Sign out
                        </Button>
                    </Box>
                ) : null}
            </Stack>

            {/* Brand profile dialog */}
            <Dialog
                open={brandDialogOpen}
                onClose={() => setBrandDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{sx: {borderRadius: '16px', maxHeight: '85vh'}}}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '16px 20px',
                    borderBottom: `1px solid ${C.border}`,
                }}>
                    <Typography sx={{fontSize: 16, fontWeight: 700, color: C.ink}}>
                        Brand Profile
                    </Typography>
                    <ButtonBase
                        onClick={() => setBrandDialogOpen(false)}
                        sx={{fontSize: 18, color: C.textSoft, cursor: 'pointer'}}>
                        x
                    </ButtonBase>
                </Box>
                <DialogContent sx={{padding: '20px'}}>
                    {brandDraft && (
                        <BrandProfileEditor
                            profile={brandDraft}
                            onChange={setBrandDraft}
                        />
                    )}
                    {brandSaveError ? (
                        <Typography sx={{mt: 1.5, fontSize: 12, color: C.coral}}>
                            {brandSaveError}
                        </Typography>
                    ) : null}
                </DialogContent>
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '10px',
                    padding: '12px 20px',
                    borderTop: `1px solid ${C.border}`,
                }}>
                    <ButtonBase
                        onClick={() => setBrandDialogOpen(false)}
                        disabled={brandSaving}
                        sx={{
                            padding: '10px 16px',
                            borderRadius: '10px',
                            border: `1px solid ${C.border}`,
                            fontFamily: 'inherit',
                            fontWeight: 600,
                            fontSize: 13,
                            color: C.textSoft,
                            cursor: 'pointer',
                        }}>
                        Cancel
                    </ButtonBase>
                    <ButtonBase
                        onClick={handleBrandSave}
                        disabled={brandSaving}
                        sx={{
                            padding: '10px 20px',
                            borderRadius: '10px',
                            background: C.teal,
                            color: '#fff',
                            fontFamily: 'inherit',
                            fontWeight: 700,
                            fontSize: 13,
                            cursor: brandSaving ? 'progress' : 'pointer',
                            opacity: brandSaving ? 0.8 : 1,
                        }}>
                        {brandSaving ? 'Saving...' : 'Save changes'}
                    </ButtonBase>
                </Box>
            </Dialog>
        </Box>
    );
}
